import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  HiCheck, HiX, HiOutlineCamera, HiOutlineExclamationCircle,
  HiOutlineClipboardList, HiOutlinePaperAirplane, HiPause,
} from 'react-icons/hi';
import { profileAPI, photoAPI, blockAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VerifiedBadge from '../components/VerifiedBadge';
import ProfilePreview from '../components/ProfilePreview';
import MyPosts from '../components/MyPosts';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { emailVerified, userId: currentUserId } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'about');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailChangeStep, setEmailChangeStep] = useState(1); // 1 = enter email, 2 = enter code
  const [changingEmail, setChangingEmail] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: 'MALE',
    interestedIn: '',
    location: '',
    latitude: null,
    longitude: null,
    bio: '',
    interests: '',
    phoneNumber: '',
    whatsappNumber: '',
    availability: '',
    height: '',
    bodyType: '',
    ethnicity: '',
    bust: '',
    eyeColor: '',
    hairColor: '',
    services: '',
    availableForTravel: false,
    hasParking: false,
  });

  useEffect(() => {
    const loadData = async () => {
      await fetchProfile();
      await fetchPhotos();
    };
    loadData();
  }, []);

  // Handle scroll to photos section if coming from ProfilePhotoGuard
  useEffect(() => {
    if (location.state?.scrollToPhotos) {
      setActiveTab('photos');
      setTimeout(() => {
        const photosSection = document.getElementById('photos-section');
        if (photosSection) {
          photosSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchProfile = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        age: response.data.age || '',
        gender: response.data.gender || 'MALE',
        interestedIn: response.data.interestedIn || '',
        location: response.data.location || '',
        latitude: response.data.latitude || null,
        longitude: response.data.longitude || null,
        bio: response.data.bio || '',
        interests: response.data.interests || '',
        phoneNumber: response.data.phoneNumber || '',
        whatsappNumber: response.data.whatsappNumber || '',
        availability: response.data.availability || '',
        height: response.data.height || '',
        bodyType: response.data.bodyType || '',
        ethnicity: response.data.ethnicity || '',
        bust: response.data.bust || '',
        eyeColor: response.data.eyeColor || '',
        hairColor: response.data.hairColor || '',
        services: response.data.services || '',
        availableForTravel: response.data.availableForTravel || false,
        hasParking: response.data.hasParking || false,
      });
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await photoAPI.getMyPhotos();
      setPhotos(response.data);
      if (response.data && response.data.length > 0) {
        // Find the primary photo, or use first photo as fallback
        const primaryPhoto = response.data.find(photo => photo.isPrimary);
        setProfilePictureUrl(primaryPhoto ? primaryPhoto.url : response.data[0].url);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e, isPrivate = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError('');
      
      if (isPrivate) {
        await photoAPI.uploadPrivatePhoto(file);
        setSuccess('Private photo uploaded successfully!');
      } else {
        await photoAPI.uploadPhoto(file);
        setSuccess('Photo uploaded successfully!');
      }
      
      await fetchPhotos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to upload photo:', err);
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCoverPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError('');
      
      await photoAPI.uploadCoverPhoto(file);
      setSuccess('Cover photo updated successfully!');
      await fetchProfile();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to upload cover photo:', err);
      setError(err.response?.data?.error || 'Failed to upload cover photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSetProfilePicture = async (photoId) => {
    try {
      await photoAPI.setProfilePicture(photoId);
      setSuccess('Profile picture updated!');
      await fetchPhotos();
      await fetchProfile(); // Refresh profile to update header
      
      // Switch to About tab to trigger sidebar reload via navigation
      setActiveTab('about');
      
      setTimeout(() => {
        setSuccess('');
        // Switch back to Photos tab
        setActiveTab('photos');
      }, 1000);
    } catch (err) {
      console.error('Failed to set profile picture:', err);
      setError('Failed to set profile picture');
    }
  };

  // Call availability toggle removed - video calls are WhatsApp-style from chat

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('Please enter your password to confirm');
      return;
    }

    try {
      setDeleting(true);
      setError('');
      
      console.log('Attempting to delete account...');
      const response = await authAPI.deleteAccount(deletePassword);
      console.log('Delete response:', response);
      
      console.log('Account deleted, clearing local storage...');
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      // Close modal
      setShowDeleteConfirm(false);
      
      console.log('Navigating to login...');
      navigate('/login');
    } catch (err) {
      console.error('Failed to delete account:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to delete account. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      // Don't close modal on error so user can see the error message
    } finally {
      setDeleting(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      setError('Please enter your password to confirm');
      return;
    }

    try {
      setDeactivating(true);
      setError('');
      
      await authAPI.deactivateAccount(deactivatePassword);
      
      // Clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      // Close modal
      setShowDeactivateConfirm(false);
      
      // Navigate to login with success message
      navigate('/login', { state: { message: 'Account deactivated. You can reactivate within 30 days by logging in.' } });
    } catch (err) {
      console.error('Failed to deactivate account:', err);
      
      let errorMessage = 'Failed to deactivate account. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setDeactivating(false);
    }
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail || !newEmail.match(/^[A-Za-z0-9+_.-]+@(.+)$/)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setChangingEmail(true);
      setError('');
      
      await authAPI.requestEmailChange(currentUserId, newEmail);
      
      setSuccess('Verification code sent to ' + newEmail);
      setEmailChangeStep(2);
    } catch (err) {
      console.error('Failed to request email change:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to send verification code';
      setError(errorMsg);
    } finally {
      setChangingEmail(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    if (!emailCode || emailCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setChangingEmail(true);
      setError('');
      
      await authAPI.verifyEmailChange(currentUserId, emailCode);
      
      setSuccess('Email address updated successfully!');
      setShowEmailChange(false);
      setNewEmail('');
      setEmailCode('');
      setEmailChangeStep(1);
      
      // Reload profile to show new email
      fetchProfile();
    } catch (err) {
      console.error('Failed to verify email change:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Invalid or expired code';
      setError(errorMsg);
    } finally {
      setChangingEmail(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    setPhotoToDelete(photoId);

  };
  
  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      await photoAPI.deletePhoto(photoToDelete);
      setSuccess('Photo deleted successfully!');
      await fetchPhotos();
      setPhotoToDelete(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to delete photo:', err);
      setError('Failed to delete photo');
      setPhotoToDelete(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updateData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        height: formData.height ? parseInt(formData.height) : null,
        phoneNumber: formData.phoneNumber || null,
        whatsappNumber: formData.whatsappNumber || null,
        availability: formData.availability || null,
        bodyType: formData.bodyType || null,
        ethnicity: formData.ethnicity || null,
        bust: formData.bust || null,
        eyeColor: formData.eyeColor || null,
        hairColor: formData.hairColor || null,
        services: formData.services || null,
        availableForTravel: formData.availableForTravel || false,
        hasParking: formData.hasParking || false,
      };

      await profileAPI.updateProfile(updateData);

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      await fetchProfile();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProfile();
    setError('');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Photo Upload Loading Overlay */}
      {uploadingPhoto && (
        <div className="photo-upload-overlay">
          <div className="photo-upload-content">
            <div className="upload-loader">
              <div className="upload-loader-circle"></div>
              <div className="upload-loader-icon" aria-hidden="true"><HiOutlineCamera /></div>
            </div>
            <div className="upload-loader-text">Uploading Photo...</div>
            <div className="upload-loader-subtext">
              Compressing and optimizing your image
            </div>
            <div className="upload-progress-bar">
              <div className="upload-progress-fill"></div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="message error-message" role="alert">
          <HiOutlineExclamationCircle aria-hidden="true" style={{ flexShrink: 0 }} /> {error}
        </div>
      )}
      {success && (
        <div className="message success-message" role="status">
          <HiCheck aria-hidden="true" style={{ flexShrink: 0 }} /> {success}
        </div>
      )}

      {/* Verification on hold - handled by admin. Self-service verification removed for now. */}

      {/* Profile Card */}
      <div className="profile-card">
        {/* Cover Photo - Only for female users */}
        {profile?.gender === 'FEMALE' && (
          <div className="profile-cover">
            {profile?.coverPhotoUrl && (
              <img src={profile.coverPhotoUrl} alt="Cover" />
            )}
            <input
              type="file"
              id="cover-photo-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleCoverPhotoUpload(e)}
            />
            <label htmlFor="cover-photo-upload" className="cover-photo-upload-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <HiOutlineCamera aria-hidden="true" /> {profile?.coverPhotoUrl ? 'Change Cover' : 'Add Cover Photo'}
            </label>
          </div>
        )}
        
        <div className="profile-header-content">
          <div className="profile-picture-section">
            <div className="profile-picture-wrapper">
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt={profile?.name} className="profile-picture" />
              ) : (
                <div className="profile-picture-placeholder">{getInitials(profile?.name)}</div>
              )}
              {/* Photo upload only for female users - male profiles are kept simple */}
              {profile?.gender === 'FEMALE' && (
                <>
                  <input
                    type="file"
                    id="profile-picture-upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handlePhotoUpload(e, false)}
                  />
                  <label htmlFor="profile-picture-upload" className="profile-picture-upload" aria-label="Upload profile picture">
                    <span className="upload-icon" aria-hidden="true"><HiOutlineCamera /></span>
                  </label>
                </>
              )}
            </div>
          </div>
          
          <div className="profile-header-info">
            <h1>
              {profile?.name}
              {profile?.hasVerifiedBadge && <VerifiedBadge size="large" />}
            </h1>
            <p className="profile-meta">
              {profile?.age && `${profile.age} years old`}
              {profile?.location && ` • ${profile.location}`}
            </p>
            
            {!isEditing && (
              <div className="profile-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </button>
                {/* Preview only for female users - male profiles are kept simple */}
                {profile?.gender === 'FEMALE' && (
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setShowPreview(true)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 5C7.02944 5 3 9.02944 3 14C3 18.9706 7.02944 23 12 23C16.9706 23 21 18.9706 21 14C21 9.02944 16.9706 5 12 5Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Preview
                  </button>
                )}
                {/* Get Verified button removed - verification handled by admin */}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="profile-tabs">
          <button 
            className={`profile-tab ${activeTab === 'about' ? 'active' : ''}`} 
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          
          {/* Photos tab only for female users - male profiles are kept simple */}
          {profile && profile.gender === 'FEMALE' && (
            <button 
              className={`profile-tab ${activeTab === 'photos' ? 'active' : ''}`} 
              onClick={() => setActiveTab('photos')}
            >
              Photos
            </button>
          )}
          
          {/* Creator mode ("Earn Money") tab removed from UI */}
          
          <button 
            className={`profile-tab ${activeTab === 'blocked' ? 'active' : ''}`} 
            onClick={() => setActiveTab('blocked')}
          >
            Blocked Users
          </button>
          
          <button 
            className={`profile-tab ${activeTab === 'settings' ? 'active' : ''}`} 
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-section">
          {activeTab === 'about' && (
            <AboutTab 
              profile={profile}
              isEditing={isEditing}
              formData={formData}
              handleChange={handleChange}
              handleSave={handleSave}
              handleCancel={handleCancel}
              saving={saving}
              navigate={navigate}
            />
          )}
          {activeTab === 'photos' && profile?.gender === 'FEMALE' && (
            <div id="photos-section">
              <PhotosTab
                photos={photos}
                uploadingPhoto={uploadingPhoto}
                handlePhotoUpload={handlePhotoUpload}
                handleDeletePhoto={handleDeletePhoto}
                handleSetProfilePicture={handleSetProfilePicture}
                profile={profile}
              />
            </div>
          )}
          {activeTab === 'blocked' && <BlockedUsersTab />}
          {activeTab === 'settings' && (
            <SettingsTab
              profile={profile}
              showDeleteConfirm={showDeleteConfirm}
              setShowDeleteConfirm={setShowDeleteConfirm}
              deletePassword={deletePassword}
              setDeletePassword={setDeletePassword}
              deleting={deleting}
              handleDeleteAccount={handleDeleteAccount}
              showDeactivateConfirm={showDeactivateConfirm}
              setShowDeactivateConfirm={setShowDeactivateConfirm}
              deactivatePassword={deactivatePassword}
              setDeactivatePassword={setDeactivatePassword}
              deactivating={deactivating}
              handleDeactivateAccount={handleDeactivateAccount}
              showEmailChange={showEmailChange}
              setShowEmailChange={setShowEmailChange}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              emailCode={emailCode}
              setEmailCode={setEmailCode}
            emailChangeStep={emailChangeStep}
            setEmailChangeStep={setEmailChangeStep}
            changingEmail={changingEmail}
            handleRequestEmailChange={handleRequestEmailChange}
            handleVerifyEmailChange={handleVerifyEmailChange}
            success={success}
            error={error}
            setError={setError}
          />
        )}
        </div>
      </div>
      
      {/* Delete Photo Confirmation Modal */}
      {photoToDelete && (
        <div className="modal-overlay" onClick={() => setPhotoToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h2 style={{ marginBottom: '16px' }}>Delete Photo?</h2>
            <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
              Are you sure you want to delete this photo? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setPhotoToDelete(null)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={confirmDeletePhoto}
              >
                Delete Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Preview Modal */}
      {showPreview && (
        <ProfilePreview 
          profile={{
            ...profile,
            photos: photos.map(p => p.url)
          }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

// About Tab Component
const AboutTab = ({ profile, isEditing, formData, handleChange, handleSave, handleCancel, saving, navigate }) => {
  
  if (!isEditing) {
    return (
      <div className="profile-section">
        {/* Earnings banner removed - video calls are free, no earnings in this phase */}

        <div className="info-card">
          <h3>Basic Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Username:</span>
              <span className="info-value">{profile?.name || 'Anonymous'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Age:</span>
              <span className="info-value">{profile?.age || 'Not set'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Gender:</span>
              <span className="info-value">{profile?.gender || 'Not set'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Location:</span>
              <span className="info-value">{profile?.location || 'Not set'}</span>
            </div>
          </div>
        </div>

        {/* Video call availability toggle removed - calls are WhatsApp-style from chat */}

        {profile?.bio && (
          <div className="info-card">
            <h3>About Me</h3>
            <p>{profile.bio}</p>
          </div>
        )}

        {profile?.interests && (
          <div className="info-card">
            <h3>Interests</h3>
            <div className="interests-tags">
              {profile.interests.split(',').map((interest, index) => (
                <span key={index} className="interest-tag">
                  {interest.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Physical Attributes - Only show for females */}
        {profile?.gender === 'FEMALE' && (
          <div className="info-card">
            <h3>Physical Attributes</h3>
          <div className="info-grid">
            {profile?.height && (
              <div className="info-item">
                <span className="info-label">Height:</span>
                <span className="info-value">{profile.height} cm</span>
              </div>
            )}
            {profile?.bodyType && (
              <div className="info-item">
                <span className="info-label">Body Type:</span>
                <span className="info-value">{profile.bodyType}</span>
              </div>
            )}
            {profile?.ethnicity && (
              <div className="info-item">
                <span className="info-label">Ethnicity:</span>
                <span className="info-value">{profile.ethnicity}</span>
              </div>
            )}
            {profile?.bust && (
              <div className="info-item">
                <span className="info-label">Bust:</span>
                <span className="info-value">{profile.bust}</span>
              </div>
            )}
            {profile?.eyeColor && (
              <div className="info-item">
                <span className="info-label">Eye Color:</span>
                <span className="info-value">{profile.eyeColor}</span>
              </div>
            )}
            {profile?.hairColor && (
              <div className="info-item">
                <span className="info-label">Hair Color:</span>
                <span className="info-value">{profile.hairColor}</span>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Services - Only show for females */}
        {profile?.gender === 'FEMALE' && profile?.services && (
          <div className="info-card">
            <h3>Services</h3>
            <p>{profile.services}</p>
          </div>
        )}
      </div>
    );
  }

  // Edit Mode
  return (
    <div className="profile-section">
      <div className="info-card">
        <h3>Edit Profile</h3>
        <form className="profile-form">
          <div className="form-section">
            <h4>Basic Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Age *</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Gender *</label>
                <select name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Interested In *</label>
                <select name="interestedIn" value={formData.interestedIn} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="MALE">Men</option>
                  <option value="FEMALE">Women</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="City, Country"
                  required
                />
              </div>
              <div className="form-group">
                <label>Age</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="18"
                  max="99"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Bio</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div className="form-group">
              <label>Interests</label>
              <input
                type="text"
                name="interests"
                value={formData.interests}
                onChange={handleChange}
                placeholder="Separate with commas (e.g., Travel, Music, Fitness)"
              />
            </div>
          </div>

          {/* Physical Attributes - Only for females */}
          {profile?.gender === 'FEMALE' && (
            <div className="form-section">
              <h4>Physical Attributes</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Height (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="e.g., 165"
                  />
                </div>
                <div className="form-group">
                  <label>Body Type</label>
                  <select name="bodyType" value={formData.bodyType} onChange={handleChange}>
                    <option value="">Select...</option>
                    <option value="Slim">Slim</option>
                    <option value="Athletic">Athletic</option>
                    <option value="Average">Average</option>
                    <option value="Curvy">Curvy</option>
                    <option value="BBW">BBW</option>
                    <option value="Muscular">Muscular</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ethnicity</label>
                  <select name="ethnicity" value={formData.ethnicity} onChange={handleChange}>
                    <option value="">Select...</option>
                    <option value="Black">Black</option>
                    <option value="White">White</option>
                    <option value="Coloured">Coloured</option>
                    <option value="Asian">Asian</option>
                    <option value="Indian">Indian</option>
                    <option value="Mixed">Mixed</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Bust Size</label>
                  <input
                    type="text"
                    name="bust"
                    value={formData.bust}
                    onChange={handleChange}
                    placeholder="e.g., 34B"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Eye Color</label>
                  <input
                    type="text"
                    name="eyeColor"
                    value={formData.eyeColor}
                    onChange={handleChange}
                    placeholder="e.g., Brown"
                  />
                </div>
                <div className="form-group">
                  <label>Hair Color</label>
                  <input
                    type="text"
                    name="hairColor"
                    value={formData.hairColor}
                    onChange={handleChange}
                    placeholder="e.g., Black"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Services - Only for females */}
          {profile?.gender === 'FEMALE' && (
            <div className="form-section">
              <h4>Services</h4>
              <div className="form-group">
                <label>Services Offered</label>
                <textarea
                  name="services"
                  value={formData.services}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Describe the services you offer..."
                />
              </div>
              
              {/* Availability Options */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="availableForTravel"
                    checked={formData.availableForTravel}
                    onChange={handleChange}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><HiOutlinePaperAirplane aria-hidden="true" style={{ transform: 'rotate(90deg)' }} /> Available to travel</span>
                </label>
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="hasParking"
                    checked={formData.hasParking}
                    onChange={handleChange}
                  />
                  <span>Parking available</span>
                </label>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Photos Tab Component
const PhotosTab = ({ photos, uploadingPhoto, handlePhotoUpload, handleDeletePhoto, handleSetProfilePicture, profile }) => {
  const isMale = profile?.gender === 'MALE';
  const isFemale = profile?.gender === 'FEMALE';
  const maxPhotos = isMale ? 1 : 6;
  const publicPhotos = photos.filter(p => !p.isPrivate);
  const privatePhotos = photos.filter(p => p.isPrivate);
  
  return (
    <div className="profile-section">
      <div className="info-card">
        <h3>My Photos ({publicPhotos.length}/{maxPhotos})</h3>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
          {isMale 
            ? 'Upload 1 profile picture.'
            : 'Upload up to 6 photos.'
          }
        </p>
        
        {/* Upload Buttons */}
        <div className="upload-photo-section" style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          {publicPhotos.length < maxPhotos && (
            <label className={`upload-photo-btn ${uploadingPhoto ? 'uploading' : ''}`} style={{ flex: 1 }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e, false)}
                disabled={uploadingPhoto}
                style={{ display: 'none' }}
              />
              {uploadingPhoto ? (
                <>
                  <span className="upload-spinner"></span>
                  Uploading...
                </>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <HiOutlineCamera aria-hidden="true" /> {isMale ? 'Upload Profile Picture' : 'Upload Photo'}
                </span>
              )}
            </label>
          )}
        </div>
        
        {/* Public Photos Section */}
        {publicPhotos.length > 0 && (
          <>
            <h4 style={{ marginTop: '24px', marginBottom: '16px', fontSize: '18px', fontWeight: '700' }}>
              Photos ({publicPhotos.length})
            </h4>
            <div className="photos-grid">
              {publicPhotos.map((photo) => (
                <div key={photo.id} className="photo-item">
                  <img src={photo.url} alt={`Photo ${photo.id}`} loading="lazy" />
                  {photo.isPrimary && <span className="primary-badge">Profile Picture</span>}
                  {!isMale && (
                    <button 
                      className={`set-primary-btn ${photo.isPrimary ? 'is-primary' : ''}`}
                      onClick={() => handleSetProfilePicture(photo.id)}
                      disabled={photo.isPrimary}
                    >
                      {photo.isPrimary ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <HiCheck aria-hidden="true" /> Profile Picture
                        </span>
                      ) : 'Set as Profile'}
                    </button>
                  )}
                  <button className="delete-photo-btn" onClick={() => handleDeletePhoto(photo.id)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        
        {photos.length === 0 && (
          <div className="no-photos">
            <p>No photos uploaded yet.</p>
          </div>
        )}
      </div>

      {/* My Posts - manage feed posts (female members) */}
      {isFemale && (
        <div className="info-card">
          <h3>My Posts</h3>
          <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Posts you've shared on the feed. You can edit or delete them here.
          </p>
          <MyPosts />
        </div>
      )}
    </div>
  );
};

// Blocked Users Tab Component
const BlockedUsersTab = () => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const response = await blockAPI.getBlockedUsers();
      setBlockedUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch blocked users:', err);
      setError('Failed to load blocked users');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      setError('');
      await blockAPI.unblockUser(userId);
      setBlockedUsers(blockedUsers.filter((u) => u.blockedUserId !== userId));
    } catch (err) {
      console.error('Failed to unblock user:', err);
      setError('Failed to unblock user. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="profile-section">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="profile-section">
      <div className="info-card">
        <h3>Blocked Users</h3>
        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
        {blockedUsers.length === 0 ? (
          <p>You haven't blocked anyone yet.</p>
        ) : (
          <div className="blocked-users-list">
            {blockedUsers.map((user) => (
              <div key={user.id} className="blocked-user-item">
                <div className="blocked-user-info">
                  {user.blockedUserAvatar ? (
                    <img src={user.blockedUserAvatar} alt={user.blockedUserName} className="blocked-user-avatar" />
                  ) : (
                    <div className="blocked-user-avatar">
                      {user.blockedUserName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <h4>{user.blockedUserName || `User #${user.blockedUserId}`}</h4>
                    <p>Blocked on {new Date(user.blockedAt).toLocaleDateString()}</p>
                    {user.reason && <p className="block-reason">Reason: {user.reason}</p>}
                  </div>
                </div>
                <button className="btn btn-secondary" onClick={() => handleUnblock(user.blockedUserId)}>
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ 
  profile, 
  showDeleteConfirm, 
  setShowDeleteConfirm, 
  deletePassword, 
  setDeletePassword, 
  deleting, 
  handleDeleteAccount,
  showDeactivateConfirm,
  setShowDeactivateConfirm,
  deactivatePassword,
  setDeactivatePassword,
  deactivating,
  handleDeactivateAccount,
  showEmailChange,
  setShowEmailChange,
  newEmail,
  setNewEmail,
  emailCode,
  setEmailCode,
  emailChangeStep,
  setEmailChangeStep,
  changingEmail,
  handleRequestEmailChange,
  handleVerifyEmailChange,
  success,
  error, 
  setError 
}) => {
  const [darkMode, setDarkMode] = React.useState(() => localStorage.getItem('darkMode') === 'true');

  React.useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  return (
  <div className="profile-section">
    <div className="info-card">
      <h3>Appearance</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div>
            <h4>Night Mode</h4>
            <p>{darkMode ? 'Dark theme is on' : 'Use a darker theme to reduce glare'}</p>
          </div>
          <label className="theme-switch" style={{ marginLeft: 'auto' }}>
            <input
              type="checkbox"
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
              aria-label="Toggle night mode"
            />
            <span className="theme-switch-slider"></span>
          </label>
        </div>
      </div>
    </div>

    <div className="info-card">
      <h3>Account Settings</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div>
            <h4>Email</h4>
            <p>{profile?.userEmail || 'Not available'}</p>
          </div>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowEmailChange(true)}
            style={{ marginLeft: 'auto' }}
          >
            Change Email
          </button>
        </div>
        <div className="setting-item">
          <div>
            <h4>Theme</h4>
            <p>Use the theme toggle in the sidebar to switch between light and dark mode</p>
          </div>
        </div>
      </div>
    </div>
    
    <div className="info-card" style={{ 
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(245, 158, 11, 0.05) 100%)', 
      border: '2px solid rgba(251, 191, 36, 0.25)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '100px',
        height: '100px',
        background: 'radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
          }} aria-hidden="true">
            <HiPause />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
              Need a Break?
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Temporarily pause your account
            </p>
          </div>
        </div>
        
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          padding: '16px', 
          borderRadius: '12px',
          marginBottom: '20px',
          border: '1px solid rgba(251, 191, 36, 0.2)'
        }}>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#f59e0b' }}>
            What happens when you deactivate:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
              <span style={{ color: '#10b981', fontSize: '16px', display: 'inline-flex' }} aria-hidden="true"><HiCheck /></span>
              <span>Your profile will be hidden from others</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
              <span style={{ color: '#10b981', fontSize: '16px', display: 'inline-flex' }} aria-hidden="true"><HiCheck /></span>
              <span>You won't receive any notifications</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
              <span style={{ color: '#10b981', fontSize: '16px', display: 'inline-flex' }} aria-hidden="true"><HiCheck /></span>
              <span>Reactivate anytime within 30 days by logging in</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
              <span style={{ color: '#10b981', fontSize: '16px', display: 'inline-flex' }} aria-hidden="true"><HiCheck /></span>
              <span>All your data remains safe and secure</span>
            </div>
          </div>
        </div>
        
        <button 
          className="btn" 
          onClick={() => setShowDeactivateConfirm(true)}
          style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            border: 'none',
            color: 'white',
            fontWeight: '700',
            padding: '14px 28px',
            fontSize: '15px',
            width: '100%',
            boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(251, 191, 36, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
          }}
        >
          Deactivate My Account
        </button>
      </div>
    </div>

    {/* Deactivate Account Modal */}
    {showDeactivateConfirm && (
      <div className="modal-overlay" onClick={() => setShowDeactivateConfirm(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '40px',
              color: 'white',
              boxShadow: '0 8px 24px rgba(251, 191, 36, 0.3)'
            }} aria-hidden="true">
              <HiPause />
            </div>
            <h2 style={{ marginBottom: '8px', fontSize: '24px', fontWeight: '800' }}>
              Take a Break from KwikLuv
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', margin: 0 }}>
              Your account will be temporarily paused, not permanently deleted
            </p>
          </div>
          
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)', 
            padding: '20px', 
            borderRadius: '16px',
            marginBottom: '24px',
            border: '2px solid rgba(251, 191, 36, 0.2)'
          }}>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <HiOutlineClipboardList aria-hidden="true" /> What You Should Know:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <div style={{
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#10b981',
                  fontWeight: '700'
                }} aria-hidden="true"><HiCheck /></div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Profile Hidden</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Your profile won't be visible to anyone
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <div style={{
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#10b981',
                  fontWeight: '700'
                }} aria-hidden="true"><HiCheck /></div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>No Notifications</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    You won't receive any emails or alerts
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <div style={{
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#10b981',
                  fontWeight: '700'
                }} aria-hidden="true"><HiCheck /></div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Easy Reactivation</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Simply log in within 30 days to reactivate
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <div style={{
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#10b981',
                  fontWeight: '700'
                }} aria-hidden="true"><HiCheck /></div>
                <div>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Data Protected</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    All your information stays safe and secure
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="error" style={{ 
              padding: '14px', 
              backgroundColor: '#fee2e2', 
              color: '#dc2626',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px', display: 'inline-flex', color: '#ef4444' }} aria-hidden="true"><HiOutlineExclamationCircle /></span>
              <span>{error}</span>
            </div>
          )}
          
          <div className="form-group" style={{ marginBottom: '24px' }}>
            <label style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '8px',
              display: 'block'
            }}>
              Confirm with your password:
            </label>
            <input
              type="password"
              value={deactivatePassword}
              onChange={(e) => setDeactivatePassword(e.target.value)}
              placeholder="Enter your password"
              autoFocus
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.05)',
                fontSize: '15px'
              }}
            />
          </div>
          
          <div className="modal-actions" style={{ gap: '12px' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowDeactivateConfirm(false);
                setDeactivatePassword('');
                setError('');
              }}
              style={{
                flex: 1,
                padding: '14px',
                fontSize: '15px',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button 
              className="btn" 
              onClick={handleDeactivateAccount} 
              disabled={deactivating || !deactivatePassword}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                border: 'none',
                color: 'white',
                fontWeight: '700',
                padding: '14px',
                fontSize: '15px',
                opacity: (deactivating || !deactivatePassword) ? 0.5 : 1,
                cursor: (deactivating || !deactivatePassword) ? 'not-allowed' : 'pointer'
              }}
            >
              {deactivating ? 'Deactivating...' : 'Confirm Deactivation'}
            </button>
          </div>
          
          <div style={{ 
            marginTop: '20px', 
            paddingTop: '20px', 
            borderTop: '1px solid var(--border-color)',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: 'var(--text-secondary)', 
              marginBottom: '12px' 
            }}>
              Want to permanently delete instead?
            </p>
            <button 
              onClick={() => {
                setShowDeactivateConfirm(false);
                setDeactivatePassword('');
                setShowDeleteConfirm(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                fontSize: '13px',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              Delete account permanently
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Delete Account Modal */}
    {showDeleteConfirm && (
      <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '56px', marginBottom: '12px', display: 'flex', justifyContent: 'center', color: '#dc2626' }} aria-hidden="true"><HiOutlineExclamationCircle /></div>
            <h2 style={{ marginBottom: '8px', color: '#dc2626' }}>Permanent Deletion</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
              This action cannot be undone
            </p>
          </div>
          
          <div style={{ 
            background: 'rgba(220, 38, 38, 0.1)', 
            padding: '16px', 
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(220, 38, 38, 0.2)'
          }}>
            <div style={{ margin: 0, fontSize: '14px', color: '#dc2626', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['All your photos will be deleted', 'All your messages will be deleted', 'Your profile will be deleted', 'This cannot be reversed'].map((item) => (
                <span key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HiX aria-hidden="true" style={{ flexShrink: 0 }} /> {item}
                </span>
              ))}
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(251, 191, 36, 0.1)', 
            padding: '14px', 
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(251, 191, 36, 0.3)'
          }}>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
              <strong style={{ color: 'rgba(59, 130, 246, 0.9)' }}>Tip:</strong> Consider deactivating instead — take a break and come back later. Your data stays safe.
            </p>
            <button 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeletePassword('');
                setShowDeactivateConfirm(true);
              }}
              style={{
                background: '#f59e0b',
                border: 'none',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: '6px',
                marginTop: '10px',
                fontWeight: '600'
              }}
            >
              Deactivate Instead
            </button>
          </div>
          
          {error && (
            <div className="error" style={{ 
              padding: '12px', 
              backgroundColor: '#fee2e2', 
              color: '#dc2626',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label>Enter your password to permanently delete:</label>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Password"
            />
          </div>
          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeletePassword('');
                setError('');
              }}
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDeleteAccount} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Change Email Modal */}
    {showEmailChange && (
      <div className="modal-overlay" onClick={() => {
        setShowEmailChange(false);
        setNewEmail('');
        setEmailCode('');
        setEmailChangeStep(1);
        setError('');
      }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <h3>{emailChangeStep === 1 ? 'Change Email Address' : 'Verify New Email'}</h3>
          
          {emailChangeStep === 1 ? (
            <>
              <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                Enter your new email address. We'll send a verification code to confirm.
              </p>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="New email address"
                className="form-input"
                style={{ width: '100%', marginBottom: '12px' }}
                autoFocus
              />
              {error && <div className="error-message" style={{ marginBottom: '12px' }}>{error}</div>}
              {success && (
                <div className="success-message" style={{ marginBottom: '12px' }}>
                  {success}
                  <p style={{ marginTop: '8px', fontSize: '13px', lineHeight: '1.5', opacity: 0.9 }}>
                    Check your spam folder if you don't see the email within 2-3 minutes.
                  </p>
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowEmailChange(false);
                    setNewEmail('');
                    setError('');
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleRequestEmailChange}
                  disabled={changingEmail}
                  style={{ flex: 1 }}
                >
                  {changingEmail ? 'Sending...' : 'Send Code'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>
                Enter the 6-digit code sent to <strong>{newEmail}</strong>
              </p>
              
              {/* Spam folder reminder */}
              <div style={{
                marginBottom: '16px',
                padding: '10px 12px',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '8px'
              }}>
                <p style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '12px', 
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Check your spam/junk folder if you don't see the email
                </p>
              </div>
              
              <input
                type="text"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="form-input"
                style={{ width: '100%', marginBottom: '12px', textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
                maxLength="6"
                autoFocus
              />
              {error && <div className="error-message" style={{ marginBottom: '12px' }}>{error}</div>}
              {success && <div className="success-message" style={{ marginBottom: '12px' }}>{success}</div>}
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setEmailChangeStep(1);
                    setEmailCode('');
                    setError('');
                  }}
                  style={{ flex: 1 }}
                >
                  Back
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleVerifyEmailChange}
                  disabled={changingEmail}
                  style={{ flex: 1 }}
                >
                  {changingEmail ? 'Verifying...' : 'Verify & Update'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
  </div>
  );
};

export default Profile;
